import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AppShell } from "@web/app/AppShell";
import { NavIcon, type NavIconName } from "./navIcons";

// One nav entry: the icon, the label, and the SAME translated string as the link's `title`. The rail
// clips the label to zero width (shell.css) rather than removing it — the label stays the link's
// accessible name in both states — and the `title` is what gives a sighted user those words back on
// hover while collapsed. Going through one component is what keeps the two provably the same string.
//
// The `t("platformNav.*")` calls stay at the call sites on purpose: src/i18n/platform-i18n.test.mjs
// scans this file for literal keys, and a `t(labelKey)` inside here would hide them all from that
// guardrail.
function NavEntry({
  to,
  icon,
  label,
  "data-testid": testId,
}: {
  to: string;
  icon: NavIconName;
  label: string;
  "data-testid"?: string;
}) {
  return (
    <NavLink to={to} title={label} data-testid={testId}>
      <NavIcon name={icon} />
      <span className="app-nav__label">{label}</span>
    </NavLink>
  );
}

// The operator console's navigation, inside the SAME app chrome the product uses (@web/app/AppShell —
// the sidebar, the top bar with the page heading, the responsive drawer and its focus trap). One shell
// for both entries is deliberate: the drawer's Esc handling and the profile popup are a11y contracts,
// and a second copy of them here would be a second place to keep correct.
//
// ONE section with two rows, and no capability branching. Every route in this app is gated on
// `platformAdmin` (./router), so a session that may see the shell at all may see both rows — unlike the
// product, where the nav is the first thing that differs between a learner and an administrator. The
// section heading is kept anyway: it names what the reader is looking at, which on a console whose two
// rows are a tenant list and a parameter table is worth saying.
export function PlatformLayout() {
  const { t } = useTranslation();

  return (
    <AppShell brandName={t("platform.brand")} nav={
      <div className="app-nav__section">
        <p className="app-nav__section-title">{t("platformNav.section")}</p>
        <NavEntry to="/tenants" data-testid="nav-tenants" icon="tenants" label={t("platformNav.tenants")} />
        <NavEntry to="/parameters" data-testid="nav-parameters" icon="parameters" label={t("platformNav.parameters")} />
      </div>
    } />
  );
}
